import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {useDisclosure} from '@mantine/hooks';
import {useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {FormViewComponentV2} from 'src/app/forms/FormViewComponent/FormViewComponentV2';
import {SidebarLinkKnowledgeBase} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {Pill} from 'src/design/atoms/Pill';
import {PublishedDraftPills} from 'src/design/atoms/pillPresets';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {
  KnowledgeArticleViewMeta,
  KnowledgeBaseDocumentTitle,
  splitKnowledgeArticleTags,
} from 'src/pages/Modules/KnowledgeBase/shared/knowledgeArticleHelpers';
import {
  useDeleteKnowledgeArticleMutation,
  useGetKnowledgeArticleQuery,
} from 'src/redux/queries/knowledgeBase';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {parseNumericParam} from 'src/shared/utils/parseNumericParam';
import type {IFormDataV2} from 'src/types/FormV2';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export const KnowledgeArticleViewPage = () => {
  const navigate = useAppNavigate();
  const {id} = useParams();
  const {setAlert} = useAlert();
  const perms = useModulePermissions(ModuleEnum.KnowledgeBase);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);
  useSetDocumentTitle(KnowledgeBaseDocumentTitle);

  const validId = parseNumericParam(id);

  const {data: article, isLoading} = useGetKnowledgeArticleQuery(validId!, {
    skip: validId === undefined,
  });

  const [deleteArticle] = useDeleteKnowledgeArticleMutation();

  const viewFormData = useMemo<IFormDataV2>(
    () => (article ? {...article} : {}),
    [article]
  );

  const tagList = useMemo(
    () => splitKnowledgeArticleTags(article?.tags),
    [article?.tags]
  );

  const handleDelete = async () => {
    confirmDeleteHandlers.close();
    if (validId === undefined) return;

    setIsDeleting(true);
    try {
      await deleteArticle(validId).unwrap();
      setAlert('Article was successfully deleted.', 'success');
      navigate(PathUtility.KnowledgeBase());
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsDeleting(false);
  };

  if (isLoading) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.KnowledgeBase}>
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </ContentWrapper>
    );
  }

  if (!article) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.KnowledgeBase}>
        <Alert color="red" title="Not Found">
          Article not found.
        </Alert>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.KnowledgeBase}>
      <Stack gap="lg">
        <BackBar
          fallbackTo={PathUtility.KnowledgeBase()}
          fallbackLabel="Back to Knowledge Base"
          breadcrumbs={[
            {
              label: KnowledgeBaseDocumentTitle,
              to: PathUtility.KnowledgeBase(),
            },
            {label: article.title},
          ]}
        />

        <PageHeader>
          <PageHeader.Title icon={SidebarLinkKnowledgeBase.icon}>
            {article.title}
          </PageHeader.Title>

          <PageHeader.Badges>
            <Pill {...PublishedDraftPills(article.published)} />
            {article.isGlobal && <Pill text="Global" color="blue" />}
          </PageHeader.Badges>

          <PageHeader.Actions>
            {perms.update && (
              <PageHeader.Action
                variant="edit"
                onClick={() =>
                  navigate(PathUtility.KnowledgeBaseEdit(article.id))
                }>
                Edit
              </PageHeader.Action>
            )}
            {perms.delete && (
              <PageHeader.Action
                variant="delete"
                onClick={() => confirmDeleteHandlers.open()}
                loading={isDeleting}>
                Delete
              </PageHeader.Action>
            )}
          </PageHeader.Actions>
        </PageHeader>

        <SectionCard title="Article">
          <Box p="md">
            <Stack gap="md">
              {tagList.length > 0 && (
                <Box>
                  <Text size="sm" fw={500} mb="xs">
                    Tags
                  </Text>
                  <Group gap="xs">
                    {tagList.map(tag => (
                      <Pill key={tag} text={tag} color="gray" />
                    ))}
                  </Group>
                </Box>
              )}
              <FormViewComponentV2
                formData={viewFormData}
                formMeta={KnowledgeArticleViewMeta}
              />
            </Stack>
          </Box>
        </SectionCard>
      </Stack>

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        title="Are you sure you want to delete this article?"
        body="This permanently removes the article from the knowledge base."
      />
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.KnowledgeBaseView(':id'),
  element: <KnowledgeArticleViewPage />,
  module: ModuleEnum.KnowledgeBase,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});
